/* Hero and document title side effects driven by the customer-stories filter selection. */
/*
 * The njks are the source of truth for the in-page hero text, the cleared-state
 * fallback title, and the document.title's siteName suffix. JS only encodes the
 * format pattern used to recompose those values when the user toggles the topic
 * filter at runtime, so the same code path runs regardless of which njk we
 * landed on initially.
 */

import { watch } from 'vue';

import { setFilter } from './stories.js';

/*
 * Hero title ("X customer stories" / "Customer stories"); mirrors the markup
 * in topic.njk's hero and falls back to the page-supplied cleared title.
 */
const getHeroTitle = (topicName, clearedTitle) => topicName
	? `${topicName} customer stories`
	: clearedTitle;

/*
 * Document.title ("Salesforce X customer stories | Gearset" / "Customer
 * stories | Gearset"); mirrors topic.njk's frontmatter title and reuses the
 * SSR'd suffix so JS never needs to know the site name.
 */
const getDocumentTitle = (topicName, clearedTitle, suffix) => topicName
	? `Salesforce ${topicName} customer stories${suffix}`
	: `${clearedTitle}${suffix}`;

/* Extract the topic slug from a /customers/{slug}/ pathname; '' for the index */
const getTopicSlugFromPath = pathname => {
	const match = pathname.match(/^\/customers\/([^/]+)\/?$/);
	return match ? match[1] : '';
};

const installTitleSync = (store, { initialTitle, clearedTitle }) => {
	/* Initialise the title that the Title.vue component renders */
	store.pageTitle = initialTitle;

	/*
	 * Snapshot the suffix (e.g. " | Gearset") from the SSR'd document.title so
	 * the helper composes the same shape the njks would have rendered, without
	 * JS having to know about the site name or the layout's title pattern.
	 */
	const lastSeparator = document.title.lastIndexOf(' | ');
	const titleSuffix = lastSeparator >= 0 ? document.title.slice(lastSeparator) : '';

	watch(
		() => {
			const key = store.filter.topics?.[0] || '';

			return {
				key,
				topicName: store.topics[key],
			};
		},
		({ key, topicName }) => {
			const path = key ? `/customers/${key}/` : '/customers/';

			/*
			 * Only push when the URL is actually changing. Skipping the push
			 * when paths already match keeps popstate-driven filter updates
			 * from adding phantom history entries on top of the entry the
			 * browser just navigated to.
			 */
			if (path !== window.location.pathname) {
				history.pushState({}, '', path);
			}

			store.pageTitle = getHeroTitle(topicName, clearedTitle);
			document.title = getDocumentTitle(topicName, clearedTitle, titleSuffix);
		},
	);

	/*
	 * Sync the store back from the URL when the user navigates with the browser's
	 * Back/Forward buttons. The filter mutation triggers the watcher above, which
	 * notices the path already matches and skips re-pushing — so the only thing
	 * that happens is the in-page title and document.title get refreshed.
	 */
	window.addEventListener('popstate', () => {
		setFilter(store, getTopicSlugFromPath(window.location.pathname), 'topics');
	});
};

export default installTitleSync;
